<template>
  <div class="col-md-12 mx-auto">
    <b-container>
      <div class="mt-4 d-flex justify-content-between">
        <back-arrow :confirm="askConfirm" :title="name"/>
      </div>
      <div class="row">
        <div class="col-md-12">
          <b-card class="mt-2 mb-4 shadow-sm">
            <b-form v-unsave-detector @submit.stop.prevent="onSubmit">
              <div class="row">
                <div class="col-md-6">
                  <b-form-group
                    :invalid-feedback="form.firstname.invalidFeedback"
                    :label="$t('contact.Firstname')"
                    :state="form.firstname.state"
                    class="medium-label mb-4"
                    label-for="firstname-input">
                    <b-form-input
                      id="firstname-input"
                      v-model="form.firstname.value"
                      :state="form.firstname.state"
                      maxlength="250"
                      required
                      type="text"
                      v-on:change="removeInvalidFeedback('firstname'); askConfirm=true;">
                    </b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-6">
                  <b-form-group
                    :invalid-feedback="form.lastname.invalidFeedback"
                    :label="$t('contact.Lastname')"
                    :state="form.lastname.state"
                    class="medium-label mb-4"
                    label-for="lastname-input">
                    <b-form-input
                      id="lastname-input"
                      v-model="form.lastname.value"
                      :state="form.lastname.state"
                      maxlength="250"
                      required
                      type="text"
                      v-on:change="removeInvalidFeedback('lastname'); askConfirm=true;">
                    </b-form-input>
                  </b-form-group>
                </div>

                <div class="col-md-4">
                  <AutocompleteField
                    :input-class="'medium-label mb-4'"
                    :input-id="'type-input'"
                    :input-label="$t('contact.Type')"
                    :items="types"
                    :search-input="form.type.value"
                    @elementValueChanged="getSelectedType">
                  </AutocompleteField>
                </div>
                <div class="col-md-4">
                  <b-form-group
                    :invalid-feedback="form.phoneNumber.invalidFeedback"
                    :label="$t('contact.PhoneNumber')"
                    :state="form.phoneNumber.state"
                    class="medium-label mb-4"
                    label-for="phoneNumber-input">
                    <b-form-input
                      id="phoneNumber-input"
                      v-model="form.phoneNumber.value"
                      :state="form.phoneNumber.state"
                      maxlength="250"
                      required
                      type="tel"
                      v-on:change="removeInvalidFeedback('phoneNumber'); askConfirm=true;">
                    </b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <b-form-group
                    :invalid-feedback="form.mail.invalidFeedback"
                    :label="$t('contact.Mail')"
                    :state="form.mail.state"
                    class="medium-label mb-4"
                    label-for="mail-input">
                    <b-form-input
                      id="mail-input"
                      v-model="form.mail.value"
                      :state="form.mail.state"
                      maxlength="250"
                      required
                      type="email"
                      v-on:change="removeInvalidFeedback('mail'); askConfirm=true;">
                    </b-form-input>
                  </b-form-group>
                </div>

                <div class="col-md-4">
                  <b-form-group
                    :invalid-feedback="form.address.invalidFeedback"
                    :label="$t('contact.Address')"
                    :state="form.address.state"
                    class="medium-label mb-4"
                    label-for="address-input">
                    <b-form-input
                      id="address-input"
                      v-model="form.address.value"
                      :state="form.address.state"
                      maxlength="250"
                      required
                      type="text"
                      v-on:change="removeInvalidFeedback('address'); askConfirm=true;">
                    </b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <b-form-group
                    :invalid-feedback="form.postalCode.invalidFeedback"
                    :label="$t('contact.PostalCode')"
                    :state="form.postalCode.state"
                    class="medium-label mb-4"
                    label-for="postal-code-input">
                    <b-form-input
                      id="postal-code-input"
                      v-model="form.postalCode.value"
                      :state="form.postalCode.state"
                      maxlength="250"
                      required
                      type="text"
                      v-on:change="removeInvalidFeedback('postalCode'); askConfirm=true;">
                    </b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-4">
                  <AutocompleteField
                    :input-class="'medium-label mb-4'"
                    :input-id="'type-input'"
                    :input-label="$t('contact.City') "
                    :items="cities"
                    :search-input="form.city.value"
                    @elementValueChanged="getSelectedCity">
                  </AutocompleteField>
                </div>
              </div>

              <div class="bold-text secondary-color level-one-title my-2">
                {{ $t('ContactDocuments') }}
              </div>

              <div v-for="(document, index) in form.documents" :key="index">
                <div class="row align-items-center">
                  <div class="col-md-4">
                    <b-form-group
                      :invalid-feedback="document.file.invalidFeedback"
                      :label="$t('ContactDocument')"
                      :label-for="`document[${index}][file]-index`"
                      :state="document.file.state"
                      class="medium-label mb-4">
                      <custom-form-file
                        :id="`document[${index}][file]-index`"
                        v-model="document.newFile"
                        :browse-text="$t('Select')"
                        :disabled="document.date.value !== null && new Date(document.date.value) <= minDate"
                        :placeholder="document.file.value ? document.file.value : $t('Select') + ' ' + $t('a') + ' ' + $t('contactDocument.file')"
                        :required="document.id == null"
                        :state="document.file.state"
                        @change="askConfirm=true;"
                        @input="setName(document)">
                      </custom-form-file>
                    </b-form-group>
                  </div>
                  <div class="col-md-4">
                    <b-form-group
                      :label="$t('contactDocument.Name')"
                      :label-for="`document[${index}][name]-index`"
                      class="medium-label mb-4">
                      <b-form-input
                        :id="`document[${index}][name]-index`"
                        v-model="document.name.value"
                        :disabled="document.date.value !== null && new Date(document.date.value) <= minDate"
                        maxlength="250"
                        required
                        type="text"
                        v-on:change="askConfirm=true;">
                      </b-form-input>
                    </b-form-group>
                  </div>
                  <div class="col-md-3">
                    <b-form-group
                      :label="$t('contactDocument.ExpirationDate')"
                      :label-for="`document[${index}][date]-index`"
                      class="medium-label mb-4">
                      <datepicker-input
                        :id="`document[${index}][date]-index`"
                        v-model="document.date.value"
                        :disabled="document.date.value !== null && new Date(document.date.value) <= minDate"
                        :min="minDate"
                        v-on:change="askConfirm=true;">
                      </datepicker-input>
                    </b-form-group>
                  </div>
                  <div class="col-md-1">
                    <b-button class="show-action-btn delete" type="button" @click="clickOnDeleteBtnDocument(index)">
                      <trash-icon size="18"/>
                    </b-button>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <span class="bold-text addLink" @click="addDocument">+ {{ $t('Add') }} {{
                      $t('contactDocument')
                    }}</span>
                </div>
              </div>

              <div class="d-flex justify-content-end">
                <b-button type="submit" variant="primary">{{ $t('Validate') }}</b-button>
              </div>
            </b-form>
          </b-card>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>

import api from '../../services/api'
import BackArrow from '../../components/b122/BackArrow'
import AutocompleteField from "../../components/b122/editFields/AutocompleteField";
import DatepickerInput from "../../components/b122/editFields/DatepickerInput.vue";
import CustomFormFile from "../../components/b122/editFields/CustomFormFile.vue";

export default {

  name: 'ContactEdit',

  components: {
    BackArrow,
    AutocompleteField,
    DatepickerInput,
    CustomFormFile
  },

  data() {
    return {
      contactId: this.$route.params.contactId,
      name: null,
      types: [],
      cities: [],
      documentNames: [],
      askConfirm: false,
      form: {
        id: null,
        firstname:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        lastname:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        type:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        phoneNumber:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        mail:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        address:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        city:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        postalCode:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        documents: []
      },
      minDate: null,
    }
  },

  mounted() {
    this.fetchData()
    this.setDate()
  },

  methods: {

    setName(document) {
      document.name.value = document.newFile.name
    },

    setDate() {
      const now = new Date()
      this.minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    },

    async fetchData() {
      await this.fetchContactInfos()
      await this.fetchContactTypes()
      await this.fetchDocumentNames()
      await this.fetchCities()
    },

    async fetchContactInfos() {
      await api
        .get(`/contact/${this.contactId}`)
        .then(response => {
          this.form.id = response.data.id
          this.form.firstname.value = response.data.firstname
          this.form.lastname.value = response.data.lastname
          this.form.type.value = response.data.type
          this.form.phoneNumber.value = response.data.phoneNumber
          this.form.mail.value = response.data.mail
          this.form.address.value = response.data.address
          this.form.city.value = response.data.city
          this.form.postalCode.value = response.data.postalCode

          response.data.documents.forEach(document => {
            this.form.documents.push({
              id: document.file.id,
              name:
                {
                  state: null,
                  invalidFeedback: null,
                  value: document.name
                },
              date:
                {
                  state: null,
                  invalidFeedback: null,
                  value: document.date
                },
              file:
                {
                  state: null,
                  invalidFeedback: null,
                  value: document.file.fileName
                },
              newFile: null,
            })
          })

          this.name = response.data.firstname + ' ' + response.data.lastname

        })
    },

    async fetchContactTypes() {
      await api
        .get(`/contact/getContactTypes`)
        .then(response => {
          response.data.forEach(type => {
            if (!this.types.includes(this.$i18n.t(type))) {
              this.types.push(this.$i18n.t(type))
            }
          })
        })
    },

    async fetchDocumentNames() {
      await api
        .get(`/contact/getDocumentNames/${this.form.id}`)
        .then(response => {
          this.documentNames = response.data
        })
    },

    async fetchCities() {
      await api
        .get(`/contact/getCities`)
        .then(response => {
          this.cities = response.data
        })
    },

    getSelectedType(data) {
      this.form.type.value = data
    },

    getSelectedCity(data) {
      this.form.city.value = data
    },

    addDocument() {
      this.form.documents.push({
        id: null,
        name:
          {
            state: null,
            invalidFeedback: null,
            value: ''
          },
        date:
          {
            state: null,
            invalidFeedback: null,
            value: null
          },
        file:
          {
            state: null,
            invalidFeedback: null,
            value: null
          }
      })
    },

    clickOnDeleteBtnDocument(index) {
      this.form.documents.splice(index, 1)
    },

    onSubmit() {
      let documentsId = []
      let documentsInfos = []
      for (let i = 0; i < this.form.documents.length; i++) {
        documentsId.push(this.form.documents[i].id)
        documentsInfos.push({
          id: this.form.documents[i].id,
          name: this.form.documents[i].name.value,
          date: this.form.documents[i].date.value
        })
      }

      let dataContact = new FormData()
      dataContact.append('firstname', this.form.firstname.value)
      dataContact.append('lastname', this.form.lastname.value)
      dataContact.append('type', this.form.type.value)
      dataContact.append('phoneNumber', this.form.phoneNumber.value)
      dataContact.append('mail', this.form.mail.value)
      dataContact.append('address', this.form.address.value)
      dataContact.append('city', this.form.city.value)
      dataContact.append('postalCode', this.form.postalCode.value)
      dataContact.append('nbDocuments', this.form.documents.length.toString())
      dataContact.append('documentsId', JSON.stringify(documentsId))
      dataContact.append('documentsInfos', JSON.stringify(documentsInfos))
      for (let i = 0; i < this.form.documents.length; i++) {
        dataContact.append(`document${i}`, this.form.documents[i].newFile)
      }
      api({
        method: 'POST',
        url: `/contact/${this.contactId}`,
        data: dataContact
      })
        .then(response => {
          this.$router.push({
            name: 'ContactShow',
            params: {
              contactId: response.data.id
            }
          }, () => {
            this.$nextTick(() => {
              this.successToast(this.form.firstname.value + ' ' + this.form.lastname.value)
            })
          })
        })
        .catch((error) => {
          if (error.response.status === 401 || error.response.status === 404) {
            this.$router.push({
              name: 'ContactEdit',
              params: {
                contactId: this.contactId
              }
            })
          } else if (error.response.status === 403) {
            const invalidField = error.response.data.invalidField
            this.form[invalidField]['state'] = false
            this.form[invalidField]['invalidFeedback'] = this.$i18n.t(error.response.data.message)
          }
        })
    },

    removeInvalidFeedback(field) {
      this.form[field]["state"] = null
    },

    successToast(contactName) {
      this.$bvToast.toast(this.$i18n.t('Contact') + ' "' + contactName + '" ' + this.$i18n.t('registered.object'), {
        autoHideDelay: 4000,
        toaster: 'b-toaster-bottom-right',
        noCloseButton: true,
        variant: "success",
        solid: true,
      })
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/views/contact/ContactEdit.vue